/**
 * cloud_light — how the clouds are lit, from the SAME light frame as the
 * ground (2026-09-25, realism §8 R6a). Pure over `lightFrame` /
 * `exposureState` (LightingModule writes them at RenderSub/4; WeatherModule
 * drives the cloud uniforms from this at 22).
 *
 *   sun colour     the atmosphere's transmittance toward the sun AT THE CLOUD
 *                  BASE (1.5 km — less air than the ground sees): white at noon,
 *                  gold, then pink and orange undersides at sunset — the biome
 *                  weather tint was the sun's colour before and is the droplets'
 *                  ALBEDO now.
 *   sun level      E0 × T × the curve's lux→scene factor × CLOUD_SUN_GAIN; the
 *                  cloud sees the sun ~1.2° after the ground loses it.
 *   ambient        tops: the drawn sky's mean radiance (greying toward the
 *                  overcast diffuse under a deck); bases: the lit ground; both
 *                  plus the night floor (moonlit clouds are brighter than the
 *                  ground — albedo 0.8 against 0.18 — but never a glow).
 *
 * The gains are CALIBRATION (a single-scatter march with a flat multi-scatter
 * approximation under-predicts a cumulus' radiance): at an equinox noon they
 * reproduce the pre-2026-09-25 cloud brightness relative to the lit ground.
 */
import { lightFrame, exposureState, E0_LUX, GROUND_ALBEDO, NIGHT_FLOOR_LUX, STARLIGHT_LUX, horizonFade, lum709, emissiveNits, type ExposureState } from '../photometry.js';
import { skyglowAt, SOURCE_MAX_KM, type LightSource } from '../light_pollution.js';
import { skyglowLive } from '../light_pollution_live.js';
import { NIGHT_SKY_NITS, OVERCAST_AMPLIFICATION, OVERCAST_NATURAL_LEFT, overcastTint } from './sky_sampler.js';
import { nightSkyUnits, naturalSkyLift } from './night_sky_model.js';

/**
 * THE CLOUDS' KEY (sky/post 2026-09-27, THE MOON §5): the light the cloud march takes as its "sun" — the sun, or the
 * moon once it outshines the sun AT THE CLOUD BASE (the ground's key flips at sunset, but the clouds see the sun ~1.2°
 * longer: their twilight undersides must not switch to the moon early). Across the crossing (a factor of 4 either
 * way) the direction, colour and level blend in log so the switch never pops. `cloudLight` writes it; the cloud
 * module's direction provider reads it (`cloudKeyDirection`). Before, a moonlit cloud was lit by the set sun: its
 * ambient took the moon's sky but its silhouette, silver lining and shading still faced the sun below the horizon.
 */
export const cloudKey = { dir: { x: 0, y: 1, z: 0 }, moonWeight: 0, /** probe A/B (`__cloudMoonKey`): false = the old sun-only key */ moonEnabled: true };
/** The cloud march's light direction this frame: the sun's until the moon outshines it at the cloud base. */
export function cloudKeyDirection(sun: { x: number; y: number; z: number }): { x: number; y: number; z: number } {
  return cloudKey.moonWeight > 0 ? cloudKey.dir : { x: sun.x, y: sun.y, z: sun.z };
}

type V3 = { x: number; y: number; z: number };
/**
 * Unit `a` → unit `b` along the SHORTER great circle, at the fraction `t` of the arc — the angle moves at a constant
 * rate in `t` (a normalised lerp crawls near the ends and races through the middle, and for antipodal ends passes
 * through the zero vector). Antipodal ends (the sun and a full moon) have no unique arc: it goes over the ZENITH side
 * (the plane holding `a` and the world up), so the key never sweeps under the ground. Writes `out`, returns it.
 */
export function slerpDir(out: V3, a: V3, b: V3, t: number): V3 {
  const la = Math.hypot(a.x, a.y, a.z) || 1, lb = Math.hypot(b.x, b.y, b.z) || 1;
  const ax = a.x / la, ay = a.y / la, az = a.z / la, bx = b.x / lb, by = b.y / lb, bz = b.z / lb;
  const cosT = Math.min(1, Math.max(-1, ax * bx + ay * by + az * bz));
  const theta = Math.acos(cosT);
  // p: the unit direction in the arc's plane perpendicular to a, toward b
  let px = bx - cosT * ax, py = by - cosT * ay, pz = bz - cosT * az;
  let pl = Math.hypot(px, py, pz);
  if (pl < 1e-6) {
    if (cosT > 0) { out.x = ax; out.y = ay; out.z = az; return out; } // the same direction
    // antipodal: the world up with a's component removed (a vertical a has no zenith side — any horizontal axis)
    px = -ay * ax; py = 1 - ay * ay; pz = -ay * az;
    pl = Math.hypot(px, py, pz);
    if (pl < 1e-6) { px = 1; py = 0; pz = 0; pl = 1; }
  }
  px /= pl; py /= pl; pz /= pl;
  const s = Math.sin(theta * t), c = Math.cos(theta * t);
  out.x = ax * c + px * s; out.y = ay * c + py * s; out.z = az * c + pz * s;
  return out;
}

/**
 * THE GAINS ARE 1 (2026-10-07, the cloud-realism work). They were 1.4 / 5 / 2 — calibration for a march whose sun term was
 * single scattering alone: the ambient (the sky's mean radiance × 5) came out as bright as the sunlit skin, so a turret's
 * lit side and its own underside differed by a factor of two (photographs: four to six) and every cloud drew flat. The
 * engine's cloud light (enginex CloudLighting) now carries the multiple scattering itself, so each input is physical:
 * the sun's illuminance AT the cloud, the sky's mean radiance on what faces up (an up-facing white surface under a sky of
 * mean radiance L shows ρ·L), the lit ground's radiance on what faces down (ρ·L_ground). The droplets' albedo is the field's.
 */
export const CLOUD_SUN_GAIN = 1;
export const CLOUD_AMB_TOP_GAIN = 1;
export const CLOUD_AMB_BOTTOM_GAIN = 1;
/** The share of the key that still reaches a heap under an overcast deck (the deck's own minimum transmittance). */
export const UNDER_DECK_SUN = 0.03;
/** The ground's horizon dips ~1.2° at the cloud base (1.5 km): sin(1.2°). */
const CLOUD_HORIZON_DIP = 0.021;
/**
 * THE HIGH DECK'S SUN (2026-10-06, the sunset-realism work): the ray-march deck sits at 3.8–9.6 km (main.ts), and from
 * its middle (6.7 km) the sun stays up until −acos(R/(R + h)) = −2.6° — over a degree after the cumulus at 1.5 km lose
 * it — through light that grazes the lower air: the red and pink undersides over a set sun (the photographs: lit clouds
 * (197, 136, 133) at −2°, (242, 127, 125) at −4°). With one 1.5 km light for every deck the high clouds went dark with
 * the low ones (the world probe: salmon streaks over grey cumulus at −2°).
 */
export const CLOUD_HIGH_KM = 6.7;
const CLOUD_HIGH_HORIZON_DIP = Math.sin(Math.sqrt((2 * CLOUD_HIGH_KM) / 6371));
/** Which deck a light is for: the cumulus and the low layer (~1–2 km), or the high ray-march deck. */
export type CloudDeck = 'low' | 'high';

type Rgb = { r: number; g: number; b: number };
export interface CloudLight { sunColor: Rgb; sunIlluminance: number; ambientTop: number; ambientBottom: number; topColor: Rgb; bottomColor: Rgb }

/** The cloud lighting for this frame, or null before the light frame exists. `albedo` is only echoed for the harness. */
export function cloudLight(_albedo: Rgb, sunDim: number, deck: CloudDeck = 'low', underDeck = 0): CloudLight | null {
  const m = lightFrame.model;
  if (!m) return null;
  const k = exposureState.luxToScene;
  const e0s = E0_LUX * k;
  const high = deck === 'high';
  const T = high ? lightFrame.cloudSunTHigh : lightFrame.cloudSunT;
  const tm = Math.max(1e-9, T.x, T.y, T.z);
  const fadeUp = horizonFade(lightFrame.sinEl + (high ? CLOUD_HIGH_HORIZON_DIP : CLOUD_HORIZON_DIP));
  let sunIlluminance = CLOUD_SUN_GAIN * e0s * tm * fadeUp * Math.max(0, 1 - sunDim);
  let sunColor = { r: T.x / tm, g: T.y / tm, b: T.z / tm };
  // THE MOON ON THE CLOUDS (sky/post 2026-09-27): its key over the cloud (the deck is the cloud itself — undo the
  // key's tCloud, as the sun's term never had it), blended in by the log ratio of the two at the cloud base.
  const mo = lightFrame.moon;
  const sd = lightFrame.sunDir;
  // The clouds' key DIRECTION is one for every deck — the low deck's call writes it; the high deck only takes its blend.
  if (!high) { cloudKey.moonWeight = 0; cloudKey.dir.x = sd.x; cloudKey.dir.y = sd.y; cloudKey.dir.z = sd.z; }
  if (cloudKey.moonEnabled && mo && mo.model.keyLux > 0 && mo.sinEl > -0.02) {
    const moonIll = (CLOUD_SUN_GAIN * mo.model.keyLux * k) / Math.max(0.05, m.tCloud);
    const t = Math.min(1, Math.max(0, (Math.log2(Math.max(1e-30, moonIll) / Math.max(1e-30, sunIlluminance)) + 2) / 4));
    const w = t * t * (3 - 2 * t);
    if (w > 0) {
      const mc = mo.model.keyColor;
      sunColor = { r: sunColor.r + (mc[0] - sunColor.r) * w, g: sunColor.g + (mc[1] - sunColor.g) * w, b: sunColor.b + (mc[2] - sunColor.b) * w };
      sunIlluminance = sunIlluminance * (1 - w) + moonIll * w;
      // gaps wave (2026-09-28): a shortest-arc SLERP, not a normalised lerp — at full moon the two are antipodal, the
      // lerp's sum shrank to ~0 near w 0.5 and its direction swung through ~180° in a sliver of w (then fell back to the sun).
      if (!high) { slerpDir(cloudKey.dir, sd, mo.dir, w); cloudKey.moonWeight = w; }
    }
  }
  // UNDER A DECK (2026-10-07): heaps that stand below an overcast layer (scud under nimbostratus, the last cumulus under
  // a closing stratocumulus) are out of the key's direct light — dark shapes against a lighter deck, lit by the ambient.
  // (`underDeck` 0 — every caller before — leaves the key whole.)
  // (a non-finite share is no deck: Math.min(1, NaN) is NaN and would have taken the sun off every cloud)
  sunIlluminance *= 1 - (Number.isFinite(underDeck) ? Math.max(0, Math.min(1, underDeck)) : 0) * (1 - UNDER_DECK_SUN);
  const c = m.cloudiness;
  const sm = lightFrame.skyMean;
  // THE CLOUDS SEE THE PHYSICAL NIGHT (the night-sky realism wave, 2026-10-06): the night floor's LIFT (NIGHT_FLOOR_LUX over
  // the moonless starlight) is the game's dark-adapted eye for the GROUND — the clouds took it too, so at every new moon
  // the whole deck glowed a lifted grey over the wilds, ~100× the natural sky behind it. A cloud's base is lit by the
  // ground's reflected starlight and its top by the airglow — a dark site's clouds are BLACK against a starry gap (Kyba
  // et al. 2011: overcast darkens a natural sky; a town's lamps are what lights a deck — the UPLIGHT below). Under a full
  // moon the lift is 0 and nothing changes (the moon's light on the clouds is untouched). `cloudNight.physical` = false
  // restores the lifted clouds (A/B, `__cloudUplight({ physical: false })`).
  const lift = cloudNight.physical ? gameNightLift() : 0;
  const skyClear = lum709(sm.x, sm.y, sm.z) * e0s;
  const skyOvercast = (m.diffuseLux / Math.PI) * k;
  const skyL = skyClear * (1 - c) + skyOvercast * c;
  const floor = (Math.max(0, (lightFrame.nightLux ?? NIGHT_FLOOR_LUX) - lift) * k) / Math.PI; // sky/post (2026-09-27): the floor + the moon's global (THE MOON); 2026-10-06: less the game's lift
  const groundL = (GROUND_ALBEDO * Math.max(0, m.globalLux - lift) * k) / Math.PI;
  const ambientTop = CLOUD_AMB_TOP_GAIN * skyL + floor;
  const ambientBottom = CLOUD_AMB_BOTTOM_GAIN * groundL + floor;
  // Hues: the sky's own (greying under a deck), a cool night tint as the floor takes over.
  const smax = Math.max(1e-9, sm.x, sm.y, sm.z);
  const day = { r: sm.x / smax, g: sm.y / smax, b: sm.z / smax };
  const grey = { r: 0.9, g: 0.93, b: 1 }, night = { r: 0.55, g: 0.66, b: 1 };
  const nightW = floor / Math.max(1e-9, ambientTop);
  const mixc = (a: Rgb, b: Rgb, t: number): Rgb => ({ r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t });
  const topColor = mixc(mixc(day, grey, c), night, Math.min(1, nightW));
  // Bases: the ground's light — the key's warmth over the sky's blue, by their share.
  const keyH = m.keyLux * Math.max(0, lightFrame.sinEl);
  const kw = keyH / Math.max(1e-6, keyH + m.diffuseLux);
  const warm = { r: m.keyColor[0], g: m.keyColor[1], b: m.keyColor[2] };
  const bottomColor = mixc(mixc(mixc(topColor, warm, kw * 0.6), { r: 0.8, g: 0.78, b: 0.74 }, 0.3), night, Math.min(1, floor / Math.max(1e-9, ambientBottom)));
  return { sunColor, sunIlluminance, ambientTop, ambientBottom, topColor, bottomColor };
}

/** Sun-cone samples per march step by tier (§8 R6a: 4–6 on high / ultra). */
export function cloudSunSteps(tier: string): number {
  return tier === 'ultra' ? 6 : tier === 'high' ? 4 : tier === 'med' ? 3 : 2;
}

// ── THE NIGHT AND THE UPLIGHT (the night-sky realism wave, 2026-10-06 — agent A) ────────────────────────────────────

/** The A/B switches: `physical` — the clouds take the physical night, not the ground's lifted floor (above). */
export const cloudNight = { physical: true };
/**
 * The game's night LIFT in lux: the part of the night floor (`lightFrame.moon.floorLux`) above the moonless starlight —
 * NIGHT_FLOOR_LUX − STARLIGHT_LUX under a new moon, 0 under a full one (the moon then carries the night itself).
 */
export function gameNightLift(): number {
  const mo = lightFrame.moon;
  return mo ? Math.max(0, mo.floorLux - STARLIGHT_LUX) : 0;
}

/**
 * THE UPLIGHT (Kyba et al. 2011, "Cloud coverage acts as an amplifier for ecological light pollution in urban
 * ecosystems": a deck over Berlin was ×10 brighter than the clear sky, ×2.8 at 32 km — its base is a white Lambertian
 * surface sending a city's upward light back down; Kyba et al. 2012: and REDDER). A cloud's base over a lit town glows
 * warm, one over the wilds does not (MSFS players' complaint: distant clouds orange over empty land). The engine cloud
 * field takes a PROVIDER (the ground's light at a cloud's absolute XZ — evaluated once per cloud) and a SCALE (scene
 * radiance per unit): the provider is the artificial sky's zenith ratio over that point (the contract: `skyglowAt`) in
 * the overcast tint; the scale is `CLOUD_UPLIGHT_GAIN` × the natural sky's luminance × the lift through `emissiveNits`
 * (THE PHYSICAL-EMISSIVE CONTRACT) × the night factor (the lamps are on). THE DRAWN SKY (2026-10-06, after agent S's
 * hand-back): the base is in the units the sky shader draws behind it — the deck's physical R × OVERCAST_AMPLIFICATION,
 * ADAPTED (night_sky_model.artificialDisplayUnits) × the lift — so a lit deck and the city sky in its gaps agree (the
 * provider carries the adapted deck ÷ the amplification, the scale the lift). The gain is calibrated so a cloud's drawn
 * base reads that drawn deck (the cloud bed: `verify:cloud-uplight-bed:probe`) — the march multiplies the uplight by the
 * droplets' albedo, the base's opacity and its decay up the dome.
 */
// Re-calibrated 30 → 15 (2026-10-06, the drawn-units bed): at 30 the thick bases read 1.99× the drawn deck (the bed's
// gate is a factor of 2 and passed at the edge); the base is linear in the gain, and a full deck is ONE surface — the
// mesh clouds' bases must read the sky shader's own deck (THE DECK / THE LIT RAIN DECK) behind them, not twice it.
// Re-calibrated 15 → 11 (2026-10-07, the cloud-realism work): the heaps' bases are level, whole and solid now (the old
// bodies were thinned by the global coverage and let part of the uplight through), so at 15 the same bed read 1.40× the
// drawn deck; 11 reads 1.03×.
export const CLOUD_UPLIGHT_GAIN = 11;
/** The decay up the dome, in e-folds base → top (the light enters the flat base and diffuses up an optically thick volume). */
export const CLOUD_UPLIGHT_FALLOFF = 3;
export const cloudUplight = { enabled: true, gain: CLOUD_UPLIGHT_GAIN, falloff: CLOUD_UPLIGHT_FALLOFF, scale: 0, nightFactor: 0, sources: 0, installs: 0, evaluations: 0 };

/**
 * The deck's DRAWN units over a zenith ratio R (at lift 1 — the scale carries the lift — ÷ the amplification: the
 * provider's unit): S's own mirror (night_sky_model.nightSkyUnits) evaluated at the zenith for an artificial sky of
 * R × OVERCAST_AMPLIFICATION, its natural part taken away — THE DRAWN SKY, never re-derived here.
 */
export function deckProviderUnits(R: number): number {
  if (!(R > 0) || !Number.isFinite(R)) return 0;
  const a = nightSkyUnits({ ratio: R * OVERCAST_AMPLIFICATION, tint: [1, 1, 1] }, [], 1, 0, 1, 0).rgb[1];
  const n = nightSkyUnits({ ratio: 0, tint: [1, 1, 1] }, [], 1, 0, 1, 0).rgb[1];
  return Math.max(0, a - n) / OVERCAST_AMPLIFICATION;
}
/**
 * THE DECK'S NATURAL SHARE (2026-10-06, the wilds-black report): a deck's base is the sky the shader draws there, and
 * under a deck the shader leaves OVERCAST_NATURAL_LEFT of the lifted natural sky (THE DECK, and the rain dome since this
 * report) — so a base carries that share too, in the provider's unit (the drawn natural zenith at lift 1 × the share ÷ the
 * amplification), with the natural sky's own tint. Without it the bases over the wilds read the physical night (~4 % of
 * the lifted sky) against a dome at 25 %, and under a full rain or snow deck the whole sky drew black.
 */
export function deckNaturalUnits(): { r: number; g: number; b: number } {
  const n = nightSkyUnits({ ratio: 0, tint: [1, 1, 1] }, [], 1, 0, 1, 0).rgb;
  const k = OVERCAST_NATURAL_LEFT / OVERCAST_AMPLIFICATION;
  return { r: n[0] * k, g: n[1] * k, b: n[2] * k };
}
/** The ground's light under a cloud at an ABSOLUTE point: the deck's drawn units over the zenith ratio there (the contract) × the overcast tint, + the deck's natural share. */
export function cloudUplightAt(absX: number, absZ: number, sources: ReadonlyArray<LightSource>): { r: number; g: number; b: number } {
  const nat = deckNaturalUnits();
  const g = skyglowAt(absX, absZ, sources);
  const D = deckProviderUnits(g.ratio);
  if (D <= 0) return nat;
  const t = overcastTint(g.tint);
  return { r: D * t[0] + nat.r, g: D * t[1] + nat.g, b: D * t[2] + nat.b };
}
/** The sky shader's display lift now (night_sky_model.naturalSkyLift — ×25 under a new moon, 1 under a bright one). */
export function liveNightLift(): number { return naturalSkyLift(lightFrame.moon?.model.globalLux ?? 0); }
/** The scene radiance one provider unit stands for this frame (0 by day, and 0 when switched off): gain × the unit × the lift. */
export function cloudUplightScale(nightFactor: number, state: Pick<ExposureState, 'evScene' | 'luxToScene'> = exposureState, lift: number = liveNightLift()): number {
  if (!cloudUplight.enabled) return 0;
  const nf = Number.isFinite(nightFactor) ? Math.max(0, Math.min(1, nightFactor)) : 0;
  const L = Number.isFinite(lift) && lift > 0 ? lift : 1;
  return emissiveNits(cloudUplight.gain * NIGHT_SKY_NITS * L, undefined, state) * nf;
}

/**
 * A provider over a source list that only looks at the sources that can reach the point: a 30 km bucket grid (one
 * build per list); a query walks the buckets within SOURCE_MAX_KM. ~375 sources, ~1,600 sprites on a re-stream.
 */
export function makeUplightProvider(sources: ReadonlyArray<LightSource>): ((x: number, z: number) => { r: number; g: number; b: number }) | null {
  // No town anywhere (outside the Commons): every base still carries the deck's natural share.
  if (!sources.length) return (x: number, z: number) => (Number.isFinite(x) && Number.isFinite(z) ? deckNaturalUnits() : { r: 0, g: 0, b: 0 });
  const CELL = 30_000;
  const grid = new Map<string, LightSource[]>();
  for (const s of sources) {
    const k = `${Math.floor(s.x / CELL)},${Math.floor(s.z / CELL)}`;
    let b = grid.get(k); if (!b) grid.set(k, (b = [])); b.push(s);
  }
  const reach = Math.ceil((SOURCE_MAX_KM * 1000) / CELL);
  const near: LightSource[] = [];
  return (x: number, z: number) => {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return { r: 0, g: 0, b: 0 };
    near.length = 0;
    const cx = Math.floor(x / CELL), cz = Math.floor(z / CELL);
    for (let i = -reach; i <= reach; i++) for (let j = -reach; j <= reach; j++) {
      const b = grid.get(`${cx + i},${cz + j}`); if (b) for (const s of b) near.push(s);
    }
    cloudUplight.evaluations++;
    return cloudUplightAt(x, z, near);
  };
}

type UplightMesh = { setUplightProvider?(fn: ((x: number, z: number) => { r: number; g: number; b: number } | null) | null): void; setUplight?(scale: number, falloff?: number): void };
type UplightDeck = { setUplight?(rgb: { r: number; g: number; b: number }, falloff?: number): void };
let installedSources: ReadonlyArray<LightSource> | null = null;
let installedOn: unknown = null;
/** The inactive case's ONE list: the latch below compares by identity, and a fresh `[]` each frame re-installed the provider (and re-streamed the far layer) every frame off the Commons. */
const NO_SOURCES: ReadonlyArray<LightSource> = [];
/**
 * Drive the clouds' uplight this frame (WeatherModule's cloud block): the provider follows the live source list (one
 * install per list / per field), the scale the exposure and the night; the screen-space deck (the fallback renderer)
 * takes the glow over the viewer as one colour.
 */
export function driveCloudUplight(cloud: { meshField?: unknown; renderer?: unknown; lowRenderer?: unknown }, nightFactor: number): void {
  const sg = skyglowLive();
  const sources = sg.active ? sg.sources : NO_SOURCES;
  const mesh = cloud.meshField as UplightMesh | null | undefined;
  if (mesh?.setUplightProvider && (sources !== installedSources || mesh !== installedOn)) {
    installedSources = sources; installedOn = mesh;
    mesh.setUplightProvider(makeUplightProvider(sources));
    cloudUplight.installs++;
  }
  const scale = cloudUplightScale(nightFactor); // the natural share lights a deck with or without a town (THE DECK'S NATURAL SHARE)
  cloudUplight.scale = scale; cloudUplight.nightFactor = nightFactor; cloudUplight.sources = sources.length;
  mesh?.setUplight?.(scale, cloudUplight.falloff);
  const t = overcastTint(sg.glow.tint);
  const D = sg.active ? deckProviderUnits(sg.glow.ratio) : 0;
  const nat = deckNaturalUnits();
  for (const d of [cloud.renderer, cloud.lowRenderer] as Array<UplightDeck | null | undefined>) d?.setUplight?.({ r: (D * t[0] + nat.r) * scale, g: (D * t[1] + nat.g) * scale, b: (D * t[2] + nat.b) * scale }, cloudUplight.falloff);
  const gl = globalThis as Record<string, unknown>;
  if (!gl.__cloudUplight) {
    gl.__cloudUplight = (o?: { enabled?: boolean; gain?: number; falloff?: number; physical?: boolean }) => {
      if (o) {
        if (typeof o.enabled === 'boolean') cloudUplight.enabled = o.enabled;
        if (typeof o.gain === 'number' && Number.isFinite(o.gain)) cloudUplight.gain = Math.max(0, o.gain);
        if (typeof o.falloff === 'number' && Number.isFinite(o.falloff)) cloudUplight.falloff = Math.max(0, o.falloff);
        if (typeof o.physical === 'boolean') cloudNight.physical = o.physical;
      }
      const s = skyglowLive();
      return { ...cloudUplight, physicalNight: cloudNight.physical, glow: +s.glow.ratio.toFixed(3), sqm: +s.glow.sqm.toFixed(2), lift: +liveNightLift().toFixed(2), deckDrawnNits: +(deckProviderUnits(s.glow.ratio) * OVERCAST_AMPLIFICATION * liveNightLift() * NIGHT_SKY_NITS).toExponential(3) };
    };
  }
}
